import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button, Heading, Text } from "@nbs/ui";
import { useAuth } from "@/hooks/useAuth";
import { isAuthErrorCode, type ReviewErrorViewModel } from "../api/errors";

export interface ReviewErrorStateProps {
  readonly error: ReviewErrorViewModel;
  readonly onRetry?: () => void;
}

/**
 * Renders the backend's stable error code, never a stack trace, SQL
 * fragment, JWT, or header. The message text itself is whatever the server
 * already sanitized (`toGraphqlError` in `packages/nbs-graphql`); this
 * component only chooses layout/title based on the code family.
 *
 * For anonymous visitors hitting a private action, surface a clear login invite.
 */
export function ReviewErrorState({ error, onRetry }: ReviewErrorStateProps) {
  const { t } = useTranslation();
  const auth = useAuth();
  const isAuth = isAuthErrorCode(error.code);
  const showLoginInvite = isAuth && !auth.isAuthenticated;

  return (
    <div className="nbs-page-state" role="alert" data-testid="review-error-state">
      <Heading level={2}>
        {isAuth ? t("review.errors.accessDeniedTitle") : t("review.errors.genericTitle")}
      </Heading>
      <Text tone="muted">{error.message}</Text>
      {showLoginInvite ? (
        <Text tone="muted" data-testid="login-required-notice">
          {t("nav.loginRequired")}{" "}
          <Link to="/login" data-testid="login-required-link">
            {t("nav.login")}
          </Link>
        </Text>
      ) : null}
      <Text size="sm" tone="subtle">
        {t("review.errors.code")}: {error.code}
        {error.correlationId ? ` · ${t("review.errors.reference")}: ${error.correlationId}` : ""}
      </Text>
      {onRetry && error.retryable ? (
        <Button variant="secondary" onClick={onRetry}>
          {t("pages.errorRetry")}
        </Button>
      ) : null}
    </div>
  );
}
